import { PageHeader } from '@ant-design/pro-layout'
import type { CreateViewInput, ViewForSiderFieldsFragment, ViewGroupFieldsFragment } from '@salescore/client-api'
import { POSTHOG_EVENTS, posthogTrackView } from '@salescore/client-base'
import { ErrorContext } from '@salescore/client-common'
import type { ViewConfig } from '@salescore/core'
import type { ModalFuncProps } from 'antd'
import { Button, Checkbox, Col, Form, Input, Row, Space } from 'antd'
import { useForm } from 'antd/es/form/Form'
import { t } from 'i18next'
import { type ReactElement, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRecoilValue } from 'recoil'

import { EmojiPickerFormItem } from '../../../components/EmojiPicker'
import { ViewGroupPicker } from '../../../components/form/ViewGroupPicker'
import { viewsContextAtom } from '../../../recoil/view/atoms'
import { useViewGroupsWithParents } from '../../../recoil/view/selectors/viewsRelatedSelector'

interface FormValue {
  name: string
  viewGroupId: string | null
  userGroupIds?: string[]
  kpiIds?: string[]
  isVisibleInAllPresets?: boolean
}

interface ViewGroupWithParents {
  viewGroup: ViewGroupFieldsFragment
  parents: ViewGroupFieldsFragment[]
}

const CreateKpiViewForm = ({
  createView,
  onAfterFinish,
  showAllPresetsOption,
  isVisibleInAllPresets,
  viewGroupId,
  viewGroupsWithParents,
}: {
  viewGroupId?: string // 呼び出し元から指定されることがないので、常時undefined
  viewGroupsWithParents: ViewGroupWithParents[]
  showAllPresetsOption: boolean
  isVisibleInAllPresets: boolean
  onAfterFinish: (view?: ViewForSiderFieldsFragment, isVisibleInAllPresets?: boolean) => void
  createView: (x: CreateViewInput) => Promise<Omit<ViewForSiderFieldsFragment, '__typename'> | undefined>
}): ReactElement => {
  const context = useRecoilValue(viewsContextAtom)
  const { t } = useTranslation()
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: viewGroupも内部で検索するのは不適当では？
  const viewGroup = viewGroupsWithParents.find((x) => x.viewGroup.id === viewGroupId)
  const [emoji, setEmoji] = useState<string | null>(null)
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: Apollo clientのloadingを使う
  const [loading, setLoading] = useState(false)
  const { throwErrors } = useContext(ErrorContext)
  const [form] = useForm<FormValue>()

  const onFinish = async () => {
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: これは意味がなさそう
    if (loading) {
      return
    }

    const values: FormValue = form.getFieldsValue()
    try {
      setLoading(true)
      const config: ViewConfig = {
        type: 'kpi',
      }
      const createdView = await createView({
        name: values.name,
        viewGroupId: values.viewGroupId,
        config,
        emoji,
        private: viewGroup?.viewGroup.private ?? false,
      })
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO: onCompletedにいれるべき内容
      onAfterFinish(createdView ?? undefined, values.isVisibleInAllPresets)
      if (createdView === undefined) {
        return
      }
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO: これ自体をhookにして外で定義して隠蔽すべきというかこれ自体を外から与えるcallbackではないか？
      posthogTrackView(POSTHOG_EVENTS.create_view_kpi, {
        viewId: createdView.id,
        prevViewId: context.prevView?.id,
        prevViewType: context.prevView?.type,
        nodeName: undefined,
      })
    } catch (error) {
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO: このエラー処理にほぼ意味がないので変更する
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      throwErrors(error as Error)
    } finally {
      setLoading(false)
    }
  }

  const initialValue: FormValue = {
    name: '',
    viewGroupId: viewGroup?.viewGroup.id ?? null,
    isVisibleInAllPresets: showAllPresetsOption ? isVisibleInAllPresets : undefined,
  }

  const typeJa = 'KPI'
  const dataE2e = `view-form-create-kpi`

  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: PageHeaderを止めてmodalのtitleにする
  return (
    <PageHeader title={t(`{{type}}を新規作成`, { type: typeJa })}>
      <Form
        form={form}
        initialValues={initialValue}
        layout="vertical"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
        onFinish={onFinish}
        data-e2e={dataE2e}
      >
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Form.Item
              name="name"
              label={t(`{{type}}名`, { type: typeJa })}
              required
              rules={[
                {
                  required: true,
                  message: t(`{{type}}名を入力してください`, { type: typeJa }),
                },
              ]}
              className={'mb-4'}
            >
              <Input disabled={false} />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item name="viewGroupId" label={t(`フォルダ`)}>
              <ViewGroupPicker type={'kpi'} isPrivate={viewGroup?.viewGroup.private ?? false} archived={false} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <EmojiPickerFormItem
              name="emoji"
              emoji={emoji}
              onSelected={(emoji) => {
                setEmoji(emoji)
              }}
            />
          </Col>
          {showAllPresetsOption && (
            <Col span={24}>
              <Form.Item name="isVisibleInAllPresets" valuePropName="checked">
                <Checkbox>{t('全てのプリセットで表示')}</Checkbox>
              </Form.Item>
            </Col>
          )}
        </Row>

        <Form.Item>
          <Row justify="end">
            <Space>
              <Button
                disabled={false}
                type="primary"
                htmlType="submit"
                loading={loading}
                data-e2e="view-form-submit-button"
              >
                {t(`新規作成`)}
              </Button>
            </Space>
          </Row>
        </Form.Item>
      </Form>
    </PageHeader>
  )
}

export const buildNewKpiFormModalProperties = ({
  showAllPresetsOption,
  isVisibleInAllPresets,
  onFinish,
}: {
  showAllPresetsOption: boolean
  isVisibleInAllPresets: boolean
  onFinish: (viewId: string, isVisibleInAllPresets: boolean) => void
}): ModalFuncProps => ({
  icon: null,
  width: '95%',
  cancelText: t(`閉じる`),
  okButtonProps: { style: { display: 'none' } },
  style: {
    top: '3%',
    padding: 0,
  },
  title: t(`KPIの新規作成`),
  closable: true,
  maskClosable: true,
  destroyOnClose: true,
  footer: <></>,
  content: (
    <ModalContent
      showAllPresetsOption={showAllPresetsOption}
      isVisibleInAllPresets={isVisibleInAllPresets}
      onFinish={onFinish}
    />
  ),
})

const ModalContent = ({
  showAllPresetsOption,
  isVisibleInAllPresets,
  onFinish,
}: {
  showAllPresetsOption: boolean
  isVisibleInAllPresets: boolean
  onFinish: (viewId: string, isVisibleInAllPresets: boolean) => void
}): ReactElement => {
  const viewGroupsWithParents = useViewGroupsWithParents()
  const { createView } = useRecoilValue(viewsContextAtom)

  return (
    <CreateKpiViewForm
      viewGroupsWithParents={viewGroupsWithParents}
      showAllPresetsOption={showAllPresetsOption}
      isVisibleInAllPresets={isVisibleInAllPresets}
      createView={async (argument) => await createView({ ...argument, shouldOpenAsTab: true })}
      onAfterFinish={(view, isVisibleInAllPresets) => {
        if (view === undefined) {
          return
        }
        onFinish(view.id, isVisibleInAllPresets ?? true)
      }}
    />
  )
}
